import React, { useEffect, useState } from 'react';
import { Avatar, Card, Col, Collapse, Empty, Row, Skeleton, Tabs, message } from 'antd';
import moment from 'moment';
import queryString from 'query-string';
import { withRouter } from 'react-router-dom';
import intl from 'react-intl-universal';
import Header from 'views/Payout/components/Header/Header';
import { getHostsForPayout } from 'utils/apis/host';
import { BLANK_IMAGE, MONTH_FORMAT } from 'utils/constants';
import { numberWithCommas } from 'utils/general';
import { buildPayoutUri } from 'utils/routes';
import { getAuditedReportsByOwner, listCalendarInventory, listOwnerUnits, listServicePackages } from '../api/ownerReporting';
import OccupancyMonthGrid from './OccupancyMonthGrid';
import PayoutV2Card from './payoutV2/PayoutV2Card';
import { inventoryForPayoutUnit, poolPackageIdsForUnits } from './payoutV2/payoutCalendarInventory';
import { mapReportToCard, shouldShowPayoutAmount } from './payoutV2/mapReportToCard';
import payoutPageStyles from 'views/Payout/Payout.module.css';
import bannerStyles from 'views/Payout/components/Body/components/PayoutDetails/PayoutDetails.module.css';
import 'views/Payout/components/Body/components/PayoutList/PayoutList.css';

const UNIT_PLACEHOLDER_IMAGE = 'https://d1nhio0ox7pgb.cloudfront.net/_img/g_collection_png/standard/32x32/hotel.png';

const unitImageUrl = unit => (unit && unit.images && unit.images[0] && unit.images[0].imageUrl) || UNIT_PLACEHOLDER_IMAGE;

const collapsePanelStyle = {
  background: '#f7f7f7',
  borderRadius: 4,
  marginBottom: 24,
  border: 0,
  overflow: 'hidden'
};

const TabPane = Tabs.TabPane;

const readCurrentUser = () => {
  try {
    const authObj = JSON.parse(localStorage.getItem('auth') || 'null');
    const user = (authObj && authObj.user) || {};
    const profile = user.userProfile || {};
    return {
      id: user._id || '',
      label: [profile.firstName, profile.lastName].filter(Boolean).join(' ') || user.username || ''
    };
  } catch (e) {
    return { id: '', label: '' };
  }
};

const toHostOptions = hosts =>
  (hosts || []).map(host => {
    const image = host.images && host.images[0] && host.images[0].imageUrl ? host.images[0].imageUrl : BLANK_IMAGE;
    const owners =
      host.owners && host.owners.length > 0
        ? host.owners.map(owner => {
            const profile = owner.userProfile || {};
            return {
              value: owner._id,
              label: [profile.firstName, profile.lastName].filter(Boolean).join(' ')
            };
          })
        : [];
    return {
      value: host._id,
      label: host.name,
      image,
      owners,
      currency: host.currency
    };
  });

const Banner = ({ shouldShowEarning, totalEarning, currency }) => (
  <Row className={bannerStyles.payoutDetailsBanner}>
    <Row className={bannerStyles.payoutDetailsBannerTitle}>{intl.get('payout.headerLabels.totalEarning').d('Total Earnings')}</Row>
    <Row className={bannerStyles.payoutDetailsBannerContent}>
      {shouldShowEarning ? (
        <span>
          {currency} <span className={bannerStyles.payoutDetailsBannerAmount}>{numberWithCommas(totalEarning)}</span>
        </span>
      ) : (
        <span className={bannerStyles.payoutDetailsBannerNoAmount}>{intl.get('payout.headerLabels.stayTuned').d('STAY TUNED')}</span>
      )}
    </Row>
  </Row>
);

const PayoutV2Page = ({ location, history }) => {
  const parsed = queryString.parse((location && location.search) || '');
  const queryHost = Array.isArray(parsed.host) ? parsed.host[0] : parsed.host;
  const queryDate = parsed.date ? moment(parsed.date) : moment();
  const initialYear = queryDate.isValid() ? queryDate.year() : moment().year();
  const initialMonth = queryDate.isValid() ? queryDate.month() + 1 : moment().month() + 1;

  const [hostOptions, setHostOptions] = useState([]);
  const [selectedHostId, setSelectedHostId] = useState(queryHost || '');
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);
  const [headerLoading, setHeaderLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [currency, setCurrency] = useState('MYR');
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [hostUnits, setHostUnits] = useState([]);
  const [calendarByUnit, setCalendarByUnit] = useState({});

  const syncUrl = (hostId, ownerId, nextYear, nextMonth) => {
    const date = moment({ year: nextYear, month: nextMonth - 1 }).format(MONTH_FORMAT);
    const next = buildPayoutUri({ host: hostId, owner: ownerId, date });
    const current = `${location.pathname}${location.search || ''}`;
    if (current !== next) {
      history.replace(next);
    }
  };

  const applyHost = (hostId, options) => {
    const found = (options || []).find(item => String(item.value) === String(hostId)) || (options || [])[0];
    if (!found) {
      setSelectedHostId('');
      setOwnerOptions([]);
      setSelectedOwnerId('');
      return { hostId: '', ownerId: '', currency: 'MYR' };
    }
    setSelectedHostId(found.value);
    setCurrency(found.currency || 'MYR');
    const currentUser = readCurrentUser();
    const owners = found.owners || [];
    const selfOwner = owners.find(owner => String(owner.value) === String(currentUser.id));
    const lockedOwner = selfOwner || (currentUser.id ? { value: currentUser.id, label: currentUser.label } : owners[0]);
    const lockedOwners = lockedOwner ? [lockedOwner] : [];
    const ownerId = lockedOwner ? lockedOwner.value : '';
    setOwnerOptions(lockedOwners);
    setSelectedOwnerId(ownerId);
    return { hostId: found.value, ownerId, currency: found.currency || 'MYR' };
  };

  useEffect(() => {
    let cancelled = false;
    setHeaderLoading(true);
    getHostsForPayout()
      .then(hosts => {
        if (cancelled) return;
        const options = toHostOptions(hosts);
        setHostOptions(options);
        const next = applyHost(queryHost, options);
        syncUrl(next.hostId, next.ownerId, year, month);
      })
      .catch(() => {
        if (!cancelled) setHostOptions([]);
      })
      .finally(() => {
        if (!cancelled) setHeaderLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedHostId) {
      setCards([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAuditedReportsByOwner(selectedHostId, { year, month })
      .then(rows => {
        if (cancelled) return;
        setCards((rows || []).map(mapReportToCard));
      })
      .catch(() => {
        if (!cancelled) {
          setCards([]);
          message.error('Failed to load payouts');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedHostId, year, month]);

  useEffect(() => {
    if (!selectedHostId || !selectedOwnerId) {
      setHostUnits([]);
      setCalendarByUnit({});
      setUnitsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setUnitsLoading(true);
    listOwnerUnits(selectedHostId, { ownedBy: selectedOwnerId })
      .then(units => {
        if (!cancelled) setHostUnits(Array.isArray(units) ? units : []);
      })
      .catch(() => {
        if (!cancelled) {
          setHostUnits([]);
          message.error('Failed to load calendar');
        }
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedHostId, selectedOwnerId]);

  const hostUnitIds = hostUnits
    .map(unit => unit && unit._id)
    .filter(Boolean)
    .map(String)
    .join(',');

  useEffect(() => {
    if (!selectedHostId || !hostUnitIds) {
      setCalendarByUnit({});
      setInventoryLoading(false);
      return undefined;
    }
    let cancelled = false;
    setInventoryLoading(true);
    const unitIds = hostUnitIds.split(',').filter(Boolean);
    const applyCalendars = (packages, unitInventoryByUnit, packageInventoryById) => {
      const next = {};
      unitIds.forEach(unitId => {
        next[unitId] = inventoryForPayoutUnit({
          unitId,
          packages,
          unitInventoryByUnit,
          packageInventoryById
        });
      });
      setCalendarByUnit(next);
    };
    listServicePackages(selectedHostId)
      .then(packages => {
        const list = Array.isArray(packages) ? packages : [];
        const poolIds = poolPackageIdsForUnits(list, unitIds);
        return Promise.all([
          Promise.resolve(list),
          listCalendarInventory(selectedHostId, { year, month, unitIds: hostUnitIds }).catch(() => ({ byUnit: {} })),
          ...poolIds.map(servicePackageId =>
            listCalendarInventory(selectedHostId, { servicePackageId, year, month })
              .then(data => ({ servicePackageId, data }))
              .catch(() => ({ servicePackageId, data: null }))
          )
        ]);
      })
      .catch(() => listCalendarInventory(selectedHostId, { year, month, unitIds: hostUnitIds }).then(data => [[], data]))
      .then(result => {
        if (cancelled || !result) return;
        const packages = result[0] || [];
        const unitData = result[1] || {};
        const packageInventoryById = {};
        result.slice(2).forEach(row => {
          if (row && row.data) packageInventoryById[row.servicePackageId] = row.data;
        });
        applyCalendars(packages, unitData.byUnit || {}, packageInventoryById);
      })
      .catch(() => {
        if (!cancelled) {
          setCalendarByUnit({});
          message.error('Failed to load calendar');
        }
      })
      .finally(() => {
        if (!cancelled) setInventoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedHostId, hostUnitIds, year, month]);

  const onMonthChange = (nextYear, monthIndex) => {
    const nextMonth = monthIndex + 1;
    setYear(nextYear);
    setMonth(nextMonth);
    syncUrl(selectedHostId, selectedOwnerId, nextYear, nextMonth);
  };

  const onHostChange = (hostId, _image, _owners) => {
    const next = applyHost(hostId, hostOptions);
    syncUrl(next.hostId, next.ownerId, year, month);
  };

  const showAmount = shouldShowPayoutAmount(year, month);
  const totalEarning = cards.reduce((sum, card) => sum + (Number(card.amountTransferToOwner) || 0), 0);
  const displayCurrency = (cards[0] && cards[0].currency) || currency || 'MYR';
  const calendarLoading = unitsLoading || inventoryLoading;

  return (
    <Card className={payoutPageStyles.card}>
      <Skeleton loading={headerLoading} active>
        <Row>
          <Header
            hostOptions={hostOptions}
            ownerOptions={ownerOptions}
            selectedHostId={selectedHostId}
            selectedOwnerId={selectedOwnerId}
            selectedOwnerName={(ownerOptions[0] && ownerOptions[0].label) || ''}
            defaultDateValue={moment({ year, month: month - 1 })}
            onMonthChange={onMonthChange}
            onHostChange={onHostChange}
            onOwnerChange={() => {}}
            isPayoutFreeze={false}
            handleOnPayoutFreeze={() => {}}
            month={month - 1}
            year={year}
            handleOnToggleModal={() => {}}
            isOwner
            checkIsAdminReadOnly={() => true}
          />
        </Row>
        <Tabs defaultActiveKey="details">
          <TabPane tab={intl.get('payout.headerLabels.detail').d('Details')} key="details">
            <Skeleton loading={loading} active>
              <Banner shouldShowEarning={showAmount} totalEarning={totalEarning} currency={displayCurrency} />
              {cards.length === 0 ? (
                <Empty description="No reports for this month" />
              ) : (
                <Row type="flex">
                  {cards.map(card => (
                    <Col key={String(card.id || card.unitId)} xs={24} sm={24} lg={12} xl={8} xxl={6}>
                      <PayoutV2Card hostId={selectedHostId} card={card} shouldShowEarning={showAmount} />
                    </Col>
                  ))}
                </Row>
              )}
            </Skeleton>
          </TabPane>
          <TabPane tab={intl.get('payout.headerLabels.calandar').d('Calendar')} key="calendar">
            <Skeleton loading={calendarLoading} active>
              {hostUnits.length === 0 ? (
                <Empty description={intl.get('payout.headerLabels.noData').d('There are no units available for this owner')} />
              ) : (
                <Collapse accordion defaultActiveKey={String(hostUnits[0]._id)} style={collapsePanelStyle}>
                  {hostUnits.map(unit => {
                    const unitId = String(unit._id);
                    const hideOccupancyBefore = (unit.contractPeriod && unit.contractPeriod.start) || null;
                    const inventory = calendarByUnit[unitId];
                    return (
                      <Collapse.Panel
                        key={unitId}
                        header={
                          <Row type="flex" className="collapseHead">
                            <Avatar src={unitImageUrl(unit)} shape="circle" size="medium" />
                            <span className="collapseTitle">{unit.name || unitId}</span>
                          </Row>
                        }
                      >
                        <OccupancyMonthGrid year={year} month={month} inventory={inventory} hideOccupancyBefore={hideOccupancyBefore} />
                      </Collapse.Panel>
                    );
                  })}
                </Collapse>
              )}
            </Skeleton>
          </TabPane>
        </Tabs>
      </Skeleton>
    </Card>
  );
};

export default withRouter(PayoutV2Page);
